'use client'
import { $api, fetchClient, getAuthHeader } from '@/services/api/client'
import { User } from '@/types/user'
import { getCookie, setCookie } from 'cookies-next/client'
import { useCallback } from 'react'
import { useToast } from './use-toast'

export const getAuthCookies = () => {
  if (typeof window === 'undefined') {
    return { accessToken: null }
  }
  const accessToken = getCookie('access_token') as string | null
  return { accessToken }
}

export function useAuth() {
  const { toast } = useToast()

  const {
    data: userData,
    error: userDataError,
    refetch: userDataRefetch,
    isLoading: userDataLoading,
  } = $api.useQuery('get', '/auth/me', {
    headers: getAuthHeader(),
  })

  const saveToken = useCallback(
    (accessToken: string) => {
      setCookie('access_token', accessToken, {
        maxAge: 3600, // 1 hour
      })

      userDataRefetch()
    },
    [userDataRefetch]
  )

  const check = useCallback(async ({ username }: { username: string }) => {
    try {
      const response = await fetchClient.POST('/auth/check', {
        body: {
          username: username,
        },
      })
      return !!response?.data?.isAvailable
    } catch (error) {
      console.error('Error checking authentication:', error)
      return false
    }
  }, [])

  async function updateUser(user: Partial<User>) {
    const userData = await fetchClient.GET('/auth/me', {
      headers: getAuthHeader(),
    })

    if (!userData?.data?.user?.id) {
      toast({
        variant: 'destructive',
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่พบข้อมูลผู้ใช้งาน',
      })
      return false
    }

    const res = await fetchClient.PUT('/user/{id}', {
      params: {
        path: {
          id: String(userData?.data?.user?.id),
        },
      },
      headers: getAuthHeader(),
      body: user,
    })

    const response = res.response
    if (response.status !== 200) {
      toast({
        variant: 'destructive',
        title: 'เกิดข้อผิดพลาด',
        description: 'ไม่สามารถอัปเดตข้อมูลผู้ใช้งานได้',
      })

      return false
    }

    toast({
      title: 'สร้างผู้ใช้แล้ว',
      description: 'ยินดีต้อนรับเข้าสู่ Contester.life',
    })

    return true
  }

  return {
    user: userData?.user,
    userDataError,
    userDataRefetch,
    isAuthenticated: !!userData,
    isLoading: userDataLoading,
    saveToken,
    updateUser,
    check,
  }
}
